import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { useOrganization } from '../contexts/OrganizationContext'
import { mediaLibraryApi } from '../services/api'
import { ROLE_HIERARCHY, ROLES } from '../constants/roles'
import CreatorPicker from '../components/CreatorPicker'
import {
  ActivityList,
  ActivityRow,
  AdvancedDisclosure,
  Alert,
  Badge,
  BoundedSplitPane,
  Button,
  ConfirmDialog,
  DataView,
  ErrorSummary,
  Field,
  FormLayout,
  FormLayoutActions,
  FormLayoutRow,
  FormLayoutSection,
  Input,
  LinkButton,
  PageHeader,
  PageLayout,
  Select,
  StatePanel,
  Switch,
  Textarea,
} from '../components/common'

const TEMPLATE_KIND = 'poster'
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function emptyForm() {
  return {
    name: '',
    template_id: '',
    creator_id: '',
    post_text: '',
    weekly_day: '6',
    publish_time: '10:00',
    unpublish_time: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    starts_at: '',
    ends_at: '',
    is_active: true,
  }
}

function dateValue(value) {
  return value ? String(value).slice(0, 10) : ''
}

function dateTime(value) {
  if (!value) return 'Not yet'
  return new Date(value).toLocaleString()
}

function timezoneValid(value) {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format()
    return true
  } catch {
    return false
  }
}

function schedulePreview(form) {
  if (!form.publish_time || !timezoneValid(form.timezone)) return null
  const publishDay = WEEKDAYS[Number(form.weekly_day)]
  const unpublish = form.unpublish_time
    ? ` It is unpublished at ${form.unpublish_time} ${form.timezone}${form.unpublish_time <= form.publish_time ? ' the following day' : ''}.`
    : ' It remains published until an operator changes it.'
  const bounds = form.starts_at || form.ends_at
    ? ` Active${form.starts_at ? ` from ${form.starts_at}` : ''}${form.ends_at ? ` through ${form.ends_at}` : ''}.`
    : ''
  return `Publishes every ${publishDay} at ${form.publish_time} ${form.timezone}.${unpublish}${bounds}`
}

function scheduleForm(schedule) {
  return {
    name: schedule.name || '',
    template_id: schedule.template_id || '',
    creator_id: schedule.creator_id || '',
    post_text: schedule.post_text || '',
    weekly_day: String(schedule.weekly_day ?? 6),
    publish_time: schedule.publish_time || '10:00',
    unpublish_time: schedule.unpublish_time || '',
    timezone: schedule.timezone || 'UTC',
    starts_at: dateValue(schedule.starts_at),
    ends_at: dateValue(schedule.ends_at),
    is_active: schedule.is_active ?? true,
  }
}

function templateAvailable(template) {
  return Boolean(template?.is_active && template?.is_schedulable)
}

export default function SchedulingPage() {
  const { currentOrg } = useOrganization()
  const slug = currentOrg?.slug
  // The Last post link opens Growth, an internal_org_admin route (App.jsx).
  const canOpenGrowth = (ROLE_HIERARCHY[currentOrg?.role] || 0) >= ROLE_HIERARCHY[ROLES.INTERNAL_ORG_ADMIN]
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedScheduleID = searchParams.get('schedule')
  const [form, setForm] = useState(emptyForm)
  const [submitted, setSubmitted] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [pendingLifecycle, setPendingLifecycle] = useState(null)

  const templatesQuery = useQuery({
    queryKey: ['media-templates', slug, TEMPLATE_KIND],
    queryFn: () => mediaLibraryApi.listTemplates(slug, { template_kind: TEMPLATE_KIND }),
    enabled: Boolean(slug),
  })
  const schedulesQuery = useQuery({
    queryKey: ['media-schedules', slug],
    queryFn: () => mediaLibraryApi.listSchedules(slug),
    enabled: Boolean(slug),
  })

  const templates = useMemo(() => templatesQuery.data?.items || [], [templatesQuery.data?.items])
  const schedules = useMemo(() => schedulesQuery.data?.items || [], [schedulesQuery.data?.items])
  const selectedSchedule = schedules.find((item) => item.id === selectedScheduleID) || null
  const selectedTemplate = templates.find((item) => item.id === form.template_id) || null
  const availableTemplates = templates.filter(templateAvailable)

  useEffect(() => {
    setForm(selectedSchedule ? scheduleForm(selectedSchedule) : emptyForm())
    setSubmitted(false)
  }, [selectedSchedule])

  useEffect(() => {
    if (selectedScheduleID && schedulesQuery.isSuccess && !selectedSchedule) {
      setSearchParams({}, { replace: true })
    }
  }, [schedulesQuery.isSuccess, selectedSchedule, selectedScheduleID, setSearchParams])

  const errors = useMemo(() => {
    const next = []
    if (!form.name.trim()) next.push('Name is required.')
    if (!form.template_id) next.push('Template is required.')
    else if (templatesQuery.isSuccess && !templateAvailable(selectedTemplate)) next.push('Choose an available template with publishable media.')
    if (!form.creator_id) next.push('Creator is required.')
    if (!form.post_text.trim()) next.push('Post text is required.')
    if (!timezoneValid(form.timezone)) next.push('Timezone must be a valid IANA timezone, such as America/New_York.')
    if (form.starts_at && form.ends_at && form.ends_at < form.starts_at) next.push('End date must be on or after start date.')
    return next
  }, [form, selectedTemplate, templatesQuery.isSuccess])
  const preview = schedulePreview(form)

  const invalidateSchedules = () => queryClient.invalidateQueries({ queryKey: ['media-schedules', slug] })
  const createMutation = useMutation({
    mutationFn: (payload) => mediaLibraryApi.createSchedule(slug, payload),
    onSuccess: (created) => {
      toast.success('Schedule created')
      invalidateSchedules()
      setSearchParams({ schedule: created.id })
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to create schedule'),
  })
  const updateMutation = useMutation({
    mutationFn: (payload) => mediaLibraryApi.updateSchedule(slug, selectedScheduleID, payload),
    onSuccess: () => {
      toast.success('Schedule updated')
      invalidateSchedules()
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to update schedule'),
  })
  const pauseMutation = useMutation({
    mutationFn: () => mediaLibraryApi.pauseSchedule(slug, selectedScheduleID),
    onSuccess: () => {
      setForm((current) => ({ ...current, is_active: false }))
      toast.success('Schedule paused')
      invalidateSchedules()
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to pause schedule'),
  })
  const deleteMutation = useMutation({
    mutationFn: (id) => mediaLibraryApi.deleteSchedule(slug, id),
    onSuccess: () => {
      toast.success('Schedule deleted')
      setPendingDelete(null)
      setSearchParams({})
      invalidateSchedules()
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to delete schedule'),
  })

  const schedulePayload = (isActive = form.is_active) => ({
    ...form,
    name: form.name.trim(),
    post_text: form.post_text.trim(),
    weekly_day: Number(form.weekly_day),
    unpublish_time: form.unpublish_time || null,
    starts_at: form.starts_at || null,
    ends_at: form.ends_at || null,
    settings: {},
    is_active: isActive,
  })

  const save = (event) => {
    event.preventDefault()
    setSubmitted(true)
    if (errors.length || templatesQuery.isLoading || templatesQuery.error) return
    const payload = schedulePayload()
    if (selectedSchedule) updateMutation.mutate(payload)
    else createMutation.mutate(payload)
  }

  const confirmLifecycle = () => {
    if (!selectedSchedule || !pendingLifecycle) return
    if (pendingLifecycle === 'pause') {
      setPendingLifecycle(null)
      pauseMutation.mutate()
      return
    }
    setForm((current) => ({ ...current, is_active: true }))
    setPendingLifecycle(null)
    updateMutation.mutate(schedulePayload(true))
  }

  if (!slug) {
    return (
      <PageLayout size="wide">
        <PageHeader title="Scheduling" context="Posts" />
        <StatePanel state="restricted" title="Select an organization" description="Choose an organization before scheduling posts." />
      </PageLayout>
    )
  }

  const submitting = createMutation.isPending || updateMutation.isPending
  const noTemplates = templatesQuery.isSuccess && !availableTemplates.length

  const editor = (
    <FormLayout
      onSubmit={save}
      errorSummary={submitted && errors.length ? (
        <ErrorSummary>
          <ul>{errors.map((error) => <li key={error}>{error}</li>)}</ul>
        </ErrorSummary>
      ) : null}
    >
      <FormLayoutSection
        title={selectedSchedule ? 'Edit schedule' : 'New schedule'}
        description="Choose the reusable media and copy this schedule publishes."
      >
        {templatesQuery.isLoading ? <StatePanel state="loading" title="Loading post templates" compact /> : null}
        {templatesQuery.error ? (
          <StatePanel state="error" title="Post templates could not load" description={templatesQuery.error.message} onRetry={templatesQuery.refetch} compact />
        ) : null}
        {noTemplates ? (
          <StatePanel
            state="empty"
            title="Create an available template first"
            description="A schedule needs an available template with publishable Library media."
            action={<LinkButton href="/dashboard/feed/posts/templates">Open post templates</LinkButton>}
            compact
          />
        ) : null}

        <Field label="Schedule name" required error={submitted && !form.name.trim() ? 'Enter a schedule name.' : null}>
          <Input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
        </Field>
        <Field
          label="Template"
          required
          error={submitted && (!form.template_id || !templateAvailable(selectedTemplate)) ? 'Choose an available post template.' : null}
        >
          <Select value={form.template_id} onChange={(event) => setForm((current) => ({ ...current, template_id: event.target.value }))}>
            <option value="">Select a template</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id} disabled={!templateAvailable(template)}>
                {template.name}{templateAvailable(template) ? '' : template.is_active ? ' — needs attention' : ' — draft'}
              </option>
            ))}
          </Select>
        </Field>
        <CreatorPicker value={form.creator_id} onChange={(creatorID) => setForm((current) => ({ ...current, creator_id: creatorID }))} />
        {submitted && !form.creator_id ? <Alert variant="error">Choose a creator.</Alert> : null}
        <Field label="Post text" required error={submitted && !form.post_text.trim() ? 'Enter the post text.' : null}>
          <Textarea value={form.post_text} onChange={(event) => setForm((current) => ({ ...current, post_text: event.target.value }))} rows={4} />
        </Field>
      </FormLayoutSection>

      <FormLayoutSection title="Recurrence" description="Set the weekly publication time in the operator timezone.">
        <FormLayoutRow columns={2}>
          <Field label="Publish day" required>
            <Select value={form.weekly_day} onChange={(event) => setForm((current) => ({ ...current, weekly_day: event.target.value }))}>
              {WEEKDAYS.map((label, value) => <option key={label} value={String(value)}>{label}</option>)}
            </Select>
          </Field>
          <Field label="Publish time" required>
            <Input type="time" value={form.publish_time} onChange={(event) => setForm((current) => ({ ...current, publish_time: event.target.value }))} />
          </Field>
        </FormLayoutRow>
        <Field label="Timezone" required error={submitted && !timezoneValid(form.timezone) ? 'Enter a valid IANA timezone, such as America/New_York.' : null}>
          <Input value={form.timezone} onChange={(event) => setForm((current) => ({ ...current, timezone: event.target.value }))} />
        </Field>

        <AdvancedDisclosure label="Publishing window">
          <FormLayoutRow columns={2}>
            <Field label="Unpublish time" optional help="Times at or before publication run the following day.">
              <Input type="time" value={form.unpublish_time} onChange={(event) => setForm((current) => ({ ...current, unpublish_time: event.target.value }))} />
            </Field>
            <span />
            <Field label="Start date" optional>
              <Input type="date" value={form.starts_at} onChange={(event) => setForm((current) => ({ ...current, starts_at: event.target.value }))} />
            </Field>
            <Field label="End date" optional error={submitted && form.starts_at && form.ends_at && form.ends_at < form.starts_at ? 'End date must be on or after start date.' : null}>
              <Input type="date" value={form.ends_at} onChange={(event) => setForm((current) => ({ ...current, ends_at: event.target.value }))} />
            </Field>
          </FormLayoutRow>
        </AdvancedDisclosure>

        {preview ? <Alert variant="info"><strong>Schedule preview:</strong> {preview}</Alert> : null}
      </FormLayoutSection>

      {!selectedSchedule ? (
        <FormLayoutSection title="Availability">
          <Switch
            checked={form.is_active}
            onChange={(checked) => setForm((current) => ({ ...current, is_active: checked }))}
            label="Create as active schedule"
            description="Inactive schedules remain saved but do not publish until resumed."
          />
        </FormLayoutSection>
      ) : null}

      <FormLayoutActions>
        {selectedSchedule ? <Button variant="secondary" onClick={() => setSearchParams({})}>New schedule</Button> : null}
        {selectedSchedule ? <Button variant="danger" onClick={() => setPendingDelete(selectedSchedule)}>Delete schedule</Button> : null}
        {selectedSchedule ? (
          <Button variant="secondary" onClick={() => setPendingLifecycle(selectedSchedule.is_active ? 'pause' : 'resume')}>
            {selectedSchedule.is_active ? 'Pause schedule' : 'Resume schedule'}
          </Button>
        ) : null}
        <Button
          type="submit"
          loading={submitting}
          disabled={Boolean(errors.length) || templatesQuery.isLoading || Boolean(templatesQuery.error) || noTemplates}
        >
          {selectedSchedule ? 'Save schedule' : 'Create schedule'}
        </Button>
      </FormLayoutActions>
    </FormLayout>
  )

  const catalog = (
    <FormLayoutSection title="Schedules" description={`${schedules.length} ${schedules.length === 1 ? 'schedule' : 'schedules'}`}>
      <DataView
        loading={schedulesQuery.isLoading}
        error={schedulesQuery.error}
        items={schedules}
        emptyTitle="No schedules yet"
        emptyDescription="Create the first weekly post schedule."
        onRetry={schedulesQuery.refetch}
      >
        <ActivityList label="Post schedules">
          {schedules.map((schedule) => {
            const failed = schedule.run_status === 'failed'
            const status = failed ? 'Failed' : schedule.is_active ? 'Active' : 'Paused'
            return (
              <ActivityRow
                as="li"
                compact
                key={schedule.id}
                title={schedule.name}
                description={failed ? schedule.last_error : null}
                meta={[
                  { label: 'Next', value: dateTime(schedule.next_run_at) },
                  { label: 'Last', value: dateTime(schedule.last_run_at) },
                ]}
                badges={<Badge tone={failed ? 'danger' : schedule.is_active ? 'success' : 'neutral'}>{status}</Badge>}
                onSelect={() => setSearchParams({ schedule: schedule.id })}
                selectLabel={`Edit ${schedule.name}`}
                actions={schedule.last_generated_post_id && canOpenGrowth ? (
                  <LinkButton size="compact" variant="ghost" href={`/dashboard/feed/growth?post=${encodeURIComponent(schedule.last_generated_post_id)}`}>Last post</LinkButton>
                ) : null}
              />
            )
          })}
        </ActivityList>
      </DataView>
    </FormLayoutSection>
  )

  return (
    <PageLayout size="wide">
      <PageHeader
        title="Scheduling"
        context="Posts"
        subtitle="Publish reusable media and post copy on a weekly schedule."
        actions={<LinkButton href="/dashboard/feed/posts/templates" variant="secondary">Post templates</LinkButton>}
      />
      <BoundedSplitPane primary={editor} inspector={catalog} inspectorLabel="Post schedules" stackInspectorOnMobile />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete schedule?"
        message={pendingDelete ? `Delete “${pendingDelete.name}”? Existing generated posts are not deleted.` : ''}
        confirmLabel="Delete schedule"
        isDestructive
        loading={deleteMutation.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => deleteMutation.mutate(pendingDelete.id)}
      />
      <ConfirmDialog
        open={Boolean(pendingLifecycle)}
        title={pendingLifecycle === 'resume' ? 'Resume schedule?' : 'Pause schedule?'}
        message={pendingLifecycle === 'resume'
          ? 'Future posts will publish automatically on this schedule.'
          : 'Future posts will not publish until this schedule is resumed. Unsaved schedule edits are not saved.'}
        confirmLabel={pendingLifecycle === 'resume' ? 'Resume schedule' : 'Pause schedule'}
        loading={pendingLifecycle === 'resume' ? updateMutation.isPending : pauseMutation.isPending}
        onCancel={() => setPendingLifecycle(null)}
        onConfirm={confirmLifecycle}
      />
    </PageLayout>
  )
}
